import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Mic, Square, Upload, Save, Loader2, FileText } from "lucide-react";
import { TranscriptEditor } from "../TranscriptEditor";
import { API } from "../../App";
import { useRecording, mmss } from "../../lib/recording";
import { PendingAudioCard } from "./PendingAudioCard";

import { LANGUAGES as LANGS } from "../../lib/languages";
const selectCls = "px-2 py-1.5 text-sm border rounded-lg bg-background border-border";

export const SessionTranscriptTab = ({ report, patient, onUpdated }) => {
  const [transcript, setTranscript] = useState(report.transcript || "");
  const [language, setLanguage] = useState(report.language || "fr");
  const [speakers, setSpeakers] = useState(report.speakers || 2);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const recordingCtx = useRecording();
  const recordingHere = recordingCtx.session?.reportId === report.report_id;
  const recordingElsewhere = !!recordingCtx.session && !recordingHere;
  const transcribingHere = recordingCtx.transcribing === report.report_id;
  const dirty = transcript.trim() !== (report.transcript || "").trim() || language !== (report.language || "fr") || speakers !== (report.speakers || 2);

  useEffect(() => { setTranscript(report.transcript || ""); }, [report.report_id, report.transcript]);

  // transcription terminée en arrière-plan pour cette séance → rafraîchir
  useEffect(() => {
    const r = recordingCtx.lastResult;
    if (r && r.reportId === report.report_id) { setTranscript(r.report.transcript || ""); onUpdated(r.report); }
  }, [recordingCtx.lastResult]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices?.().then((list) => {
      const mics = list.filter((d) => d.kind === "audioinput");
      setDevices(mics);
      if (mics[0] && !deviceId) setDeviceId(mics[0].deviceId);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (extra = {}) => {
    setBusy("save");
    try {
      const res = await axios.put(`${API}/session-reports/${report.report_id}`, { transcript, language, speakers, ...extra }, { withCredentials: true });
      onUpdated(res.data);
      toast.success("Transcription enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const transcribeBlob = async (blob, name) => {
    setBusy("transcribe");
    try {
      const fd = new FormData();
      fd.append("audio", blob, name);
      fd.append("language", language);
      fd.append("speakers", String(speakers));
      const res = await axios.post(`${API}/session-reports/transcribe-audio`, fd, { withCredentials: true });
      setTranscript((t) => (t.trim() ? `${t.trim()}\n\n${res.data.transcription}` : res.data.transcription));
      toast.success("Transcription terminée — pensez à enregistrer");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de transcription"); }
    finally { setBusy(""); }
  };

  const startRecording = async () => {
    if (dirty) await save();
    try {
      const nr = report.session_number ? `Nr ${report.session_number}` : "séance";
      await recordingCtx.start({ reportId: report.report_id, patientId: report.patient_id, label: `${patient?.name?.split(" ")[0] || "Patient"} · ${nr}`, language, speakers }, deviceId);
      navigator.mediaDevices.enumerateDevices().then((list) => setDevices(list.filter((d) => d.kind === "audioinput"))).catch(() => {});
      toast.info("Enregistrement démarré : il continue si vous naviguez dans l'app. Arrêtez-le depuis la pastille rouge.");
    } catch (e) { toast.error(e.message?.includes("déjà") ? e.message : "Micro inaccessible : vérifiez les autorisations du navigateur"); }
  };

  const importFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    const fd = new FormData();
    if (["txt", "pdf"].includes(ext)) {
      setBusy("import");
      try {
        fd.append("document", file, file.name);
        const res = await axios.post(`${API}/session-reports/extract-text`, fd, { withCredentials: true });
        setTranscript((t) => (t.trim() ? `${t.trim()}\n\n${res.data.text}` : res.data.text));
        toast.success(`${file.name} importé — pensez à enregistrer`);
      } catch (e) { toast.error(e.response?.data?.detail || "Import impossible"); }
      finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
    } else {
      await transcribeBlob(file, file.name);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const locked = busy !== "" || recordingHere || transcribingHere;

  return (
    <div className="space-y-4" data-testid="session-transcript-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Langue</Label><br />
          <select className={selectCls} value={language} onChange={(e) => setLanguage(e.target.value)} disabled={recordingHere} data-testid="transcript-language-select">
            {LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select></div>
        <div><Label className="text-xs">Intervenants</Label><br />
          <select className={selectCls} value={speakers} onChange={(e) => setSpeakers(Number(e.target.value))} disabled={recordingHere} data-testid="transcript-speakers-select">
            {[2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
          </select></div>
        <div className="min-w-[200px] flex-1"><Label className="text-xs">Microphone</Label><br />
          <select className={`${selectCls} w-full`} value={deviceId} onChange={(e) => setDeviceId(e.target.value)} disabled={recordingHere} data-testid="transcript-mic-select">
            {devices.length === 0 && <option value="">Micro par défaut</option>}
            {devices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Micro ${i + 1}`}</option>)}
          </select></div>
        {!recordingHere ? (
          <Button onClick={startRecording} disabled={busy !== "" || recordingElsewhere || transcribingHere} className="gap-2" title={recordingElsewhere ? "Un enregistrement est déjà en cours pour une autre séance" : ""} data-testid="transcript-record-btn"><Mic className="w-4 h-4" /> Enregistrer en direct</Button>
        ) : (
          <Button onClick={recordingCtx.stop} variant="destructive" className="gap-2 animate-pulse" data-testid="transcript-stop-btn"><Square className="w-4 h-4" /> Arrêter · {mmss(recordingCtx.elapsed)}</Button>
        )}
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={locked} className="gap-2" data-testid="transcript-import-btn"><Upload className="w-4 h-4" /> Importer (.txt, .pdf, audio)</Button>
        <input ref={fileRef} type="file" accept=".txt,.pdf,.mp3,.m4a,.wav,.webm,.ogg,.mp4" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} data-testid="transcript-file-input" />
      </div>
      {recordingHere && <p className="text-xs text-muted-foreground" data-testid="transcript-recording-hint">Enregistrement en cours : vous pouvez consulter d'autres onglets ou patients, il continue en arrière-plan. À l'arrêt, la transcription est lancée puis sauvegardée automatiquement dans cette séance.</p>}
      <PendingAudioCard reportId={report.report_id} disabled={recordingHere || busy !== ""} />
      {(busy === "transcribe" || transcribingHere) && <p className="text-sm text-primary flex items-center gap-2" data-testid="transcript-busy"><Loader2 className="w-4 h-4 animate-spin" /> Transcription en cours (Gemini)… cela peut prendre 1 à 3 minutes.</p>}
      {busy === "import" && <p className="text-sm text-primary flex items-center gap-2"><FileText className="w-4 h-4 animate-pulse" /> Lecture du document…</p>}
      <TranscriptEditor value={transcript} onChange={setTranscript} disabled={locked} rows={14} testId="session-transcript" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground">{transcript.trim() ? `${transcript.trim().length.toLocaleString("fr-FR")} caractères${dirty ? " · modifications non enregistrées" : ""}` : "Aucune transcription — enregistrez, collez ou importez."}</span>
        <Button onClick={() => save()} disabled={locked || !dirty} className="gap-2" data-testid="transcript-save-btn"><Save className="w-4 h-4" /> {busy === "save" ? "Enregistrement..." : "Enregistrer la transcription"}</Button>
      </div>
    </div>
  );
};
